import { useMemo, useState } from "react";
import moment from "moment";
import { ListBoxItem } from "react-aria-components";
import { Trans, useTranslation } from "react-i18next";
import { MdRotateLeft } from "react-icons/md";
import { css } from "../../../../styled-system/css";
import { Flex, HStack, VStack, styled } from "../../../../styled-system/jsx";
import { Button, LinkButton } from "../../core/basic/Button";
import { Tab, TabList, TabPanel, Tabs } from "../../core/basic/Tabs";
import Loading from "../../core/blocks/Loading";
import { Select } from "../../fields/base/Select";
import { SearchInput } from "../../fields/base/TextField";
import { commaSearchFilterFn } from "../../table/filterFns";
import { RT8Pagination } from "../../table/Pagination";
import { Table } from "../../table/Table";
import { TableErrorWrapper } from "../../table/TableErrorWrapper";
import { createAppColumnHelper, useClientTable } from "../../table/tableFeatures";
import { usePersistedTableState } from "../../table/usePersistedTableState";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import { initAmazonAdvertisingAdditionalAccount } from "../../../modules/onboarding/amazon/initAmazonAdvertisingOAuth";
import { snakeCaseToSentenceCase } from "../../../utils/formatText";
import { SellerIdLink } from "./SellerIdLink";
import {
  type AmazonAdsClientProfiles,
  type AmazonProfile,
  profileKey,
  useImportableAmazonProfiles,
  useImportableAmazonProfilesByClient,
} from "./useAmazonProfiles";

const columnHelper = createAppColumnHelper<AmazonProfile>();

const ALL_ACCOUNTS = "all";

interface AccountOption {
  value: number | typeof ALL_ACCOUNTS;
  label: string;
  /** Who connected it and when, as the Ad Credentials tab words it. */
  byline: string;
  count: number;
  justConnected?: boolean;
}

const truncate = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } as const;

/** The account and who added it when, beside how many profiles it can import. */
function AccountOptionContent({ option }: { option: AccountOption }) {
  const { t } = useTranslation(["onboarding"]);
  return (
    <HStack gap="medium" minWidth="0">
      <VStack alignItems="stretch" gap="xxsmall" flex="1" minWidth="0">
        <HStack gap="small" minWidth="0">
          <span className={css({ color: "darkblue", fontSize: "medium", fontWeight: 600, ...truncate })}>
            {option.label}
          </span>
          {option.justConnected && (
            <span
              className={css({
                flexShrink: 0,
                borderRadius: "full",
                paddingX: "small",
                color: "green.700",
                fontSize: "xsmall",
                fontWeight: 600,
                backgroundColor: "green.600/15",
              })}
            >
              {t(($) => $.onboarding["Just connected"])}
            </span>
          )}
        </HStack>
        {option.byline && (
          <span className={css({ color: "text.secondary", fontSize: "small", ...truncate })}>{option.byline}</span>
        )}
      </VStack>
      <span className={css({ flexShrink: 0, color: "darkblue/70", fontSize: "small", fontWeight: 600 })}>
        {t(($) => $.onboarding["{count, plural, available profiles}"], { count: option.count })}
      </span>
    </HStack>
  );
}

/** Who connected the account (name, then email) over its ID. They run long, so the full details show on hover. */
function AccountCell({ client }: { client: AmazonAdsClientProfiles | undefined }) {
  const lines = [client?.amazon_user_name, client?.amazon_user_email, client?.amazon_user_id].filter(Boolean);
  return (
    <VStack alignItems="stretch" gap="0" minWidth="0" title={lines.join("\n")}>
      {lines.map((line, index) => (
        <span
          key={index}
          className={css(index === 0 ? truncate : { color: "text.secondary", fontSize: "small", ...truncate })}
        >
          {line}
        </span>
      ))}
    </VStack>
  );
}

const accountItemStyles = css({
  cursor: "pointer",
  borderLeft: "3px solid transparent",
  paddingY: "medium",
  paddingX: "large",
  outline: "none",
  _hover: { backgroundColor: "black/5" },
  _focusVisible: { backgroundColor: "black/5" },
  _selected: { borderLeftColor: "trellisPalette.4", backgroundColor: "trellisPalette.4/8" },
});

// Room on the right so a scrollbar, overlaid on macOS, never covers the pager or a row's last column.
const tabPanelStyles = css({
  flex: "1",
  minHeight: "0",
  overflowY: "auto",
  scrollbarGutter: "stable",
  paddingRight: "medium",
});

const Message = styled("p", {
  base: { textAlign: "center", fontSize: "large", color: "text.secondary", paddingX: "xlarge", paddingY: "xlarge" },
});

const credentialDetailStyles = css({ margin: "0", color: "text.secondary", fontSize: "small" });

/** The connected Amazon Ads logins, numbered in the order they were added. */
function CredentialList({ clients, scopedOnly = false }: { clients: AmazonAdsClientProfiles[]; scopedOnly?: boolean }) {
  const { t } = useTranslation(["onboarding"]);
  const fmt = useDateFormatter("long");

  return (
    <ol className={css({ listStyle: "none", margin: "0", padding: "0" })}>
      {clients.map((client, index) => {
        const { client_id, created_on, amazon_user_id, amazon_user_email, amazon_user_name } = client;
        const label =
          created_on ?
            t(($) => $.onboarding["Added on {date}"], { date: fmt.format(moment(created_on.slice(0, 10)).toDate()) })
          : "";
        const hasProfileScope = !!(amazon_user_id || amazon_user_email || amazon_user_name);
        if (scopedOnly && !amazon_user_id) {
          return null;
        }

        return (
          <li
            key={client_id}
            className={css({
              display: "flex",
              flexDirection: "column",
              gap: "xxsmall",
              paddingY: "large",
              borderBottom: "1px solid {colors.border.default}",
              _last: { borderBottom: "none" },
            })}
          >
            <h3 className={css({ color: "darkblue", fontSize: "medium", fontWeight: 600, margin: "0" })}>
              {t(($) => $.onboarding["Amazon Advertising Credential #{number}"], { number: index + 1 })}
            </h3>
            {hasProfileScope && (
              <p className={credentialDetailStyles}>
                {t(($) => $.onboarding["Account ID: {id}"], { id: amazon_user_id })}
              </p>
            )}
            <p className={credentialDetailStyles}>
              {hasProfileScope ?
                t(($) => $.onboarding["{label} by {name} ({email})"], {
                  label,
                  name: amazon_user_name,
                  email: amazon_user_email,
                })
              : label}
            </p>
          </li>
        );
      })}
    </ol>
  );
}

export function AddAmazonProfilesDialogue({
  onClose,
  onSubmit,
  isSubmitting,
  justConnectedAccount = false,
}: {
  onClose: () => void;
  onSubmit: (profiles: AmazonProfile[]) => void;
  isSubmitting: boolean;
  /** Opened right after connecting another Amazon account: start on that account's profiles. */
  justConnectedAccount?: boolean;
}) {
  const { t } = useTranslation(["onboarding", "common"]);
  const fmt = useDateFormatter("long");

  const importableByClient = useImportableAmazonProfilesByClient();
  const clients = importableByClient.data;
  const hasClients = !!clients?.length;
  // Without clients, fall back to the profiles retrieved from ASI tokens, which are already limited to
  // valid payment methods. Fetched only then, since it calls Amazon too.
  const importableProfiles = useImportableAmazonProfiles({ enabled: clients?.length === 0 });
  const error = importableByClient.error ?? (hasClients ? null : importableProfiles.error);

  // One row per importable profile, across every Amazon Ads client.
  const rows = useMemo((): AmazonProfile[] | undefined => {
    if (!clients?.length) {
      return clients && importableProfiles.data;
    }
    return clients.flatMap((client) => client.profiles.filter((profile) => profile.valid_payment_method));
  }, [clients, importableProfiles.data]);
  const isLoading = !error && !rows;

  const hasRemoteProfiles =
    hasClients ? clients.some((client) => client.profiles.length > 0) : !!importableProfiles.data?.length;

  // Agencies connect an Amazon account per client and can end up with hundreds of profiles, so the table can be
  // narrowed to one connected account, newest first. A dialog opened right after connecting an account starts on it.
  const [startOnNewest] = useState(justConnectedAccount);

  const accountOptions = useMemo(() => {
    const options = (clients ?? []).flatMap((client, index) => {
      const count = client.profiles.filter((profile) => profile.valid_payment_method).length;
      if (!count) {
        return [];
      }
      const { client_id, created_on, amazon_user_id, amazon_user_email, amazon_user_name } = client;
      const addedOn =
        created_on ?
          t(($) => $.onboarding["Added on {date}"], { date: fmt.format(moment(created_on.slice(0, 10)).toDate()) })
        : "";
      return [
        {
          value: client_id,
          label:
            amazon_user_id ?
              t(($) => $.onboarding["Amazon Advertising Account ID: {id}"], { id: amazon_user_id })
            : t(($) => $.onboarding["Amazon Advertising Credential #{number}"], { number: index + 1 }),
          byline:
            !(amazon_user_email || amazon_user_name) ? addedOn
            : addedOn ?
              t(($) => $.onboarding["{label} by {name} ({email})"], {
                label: addedOn,
                name: amazon_user_name,
                email: amazon_user_email,
              })
            : t(($) => $.onboarding["Added by {name} ({email})"], { name: amazon_user_name, email: amazon_user_email }),
          createdOn: created_on ?? "",
          count,
        },
      ];
    });
    options.sort((a, b) => b.createdOn.localeCompare(a.createdOn));
    const accounts = options.map(({ createdOn: _createdOn, ...option }, index): AccountOption => ({
      ...option,
      justConnected: startOnNewest && index === 0,
    }));
    if (accounts.length < 2) {
      return accounts;
    }
    const all: AccountOption = {
      value: ALL_ACCOUNTS,
      label: t(($) => $.onboarding["All connected accounts"]),
      byline: t(($) => $.onboarding["{count, plural, one {# account} other {# accounts}}"], { count: options.length }),
      count: options.reduce((sum, option) => sum + option.count, 0),
    };
    return [all, ...accounts];
  }, [clients, t, fmt, startOnNewest]);
  // Past "All connected accounts", the first option is the newest account.
  const newest = accountOptions.length > 1 ? accountOptions[1]?.value : undefined;
  const defaultAccount = startOnNewest && typeof newest === "number" ? newest : undefined;

  return (
    <VStack alignItems="stretch" gap="large" paddingX={{ base: "medium", xl: "xlarge" }} minHeight="200px">
      <p className={css({ textAlign: "center", fontSize: "large" })}>
        {t(($) => $.onboarding["Select the Amazon profiles from the table below that you would like to import."])}
      </p>

      <TableErrorWrapper error={error} userMessage={t(($) => $.onboarding["Error loading profiles"])}>
        {isLoading ?
          <Loading fullPage={false} height="250px" />
        : hasRemoteProfiles ?
          <ProfilePicker
            rows={rows ?? []}
            clients={clients ?? []}
            accountOptions={accountOptions}
            defaultAccount={defaultAccount}
            onClose={onClose}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
          />
        : <>
            <Message>
              {t(
                ($) =>
                  $.onboarding[
                    "We couldn't find any available remote profiles in your connected Amazon Advertising accounts. If profiles are visible in your Amazon Advertising console, or appear in the Amazon Profile Summary section after clicking \"Check Amazon Profiles\" but can't be imported, please reach out to Customer Success."
                  ],
              )}
            </Message>
            <CredentialList clients={clients ?? []} scopedOnly />
            <p className={css({ textAlign: "center", fontSize: "large" })}>
              <Trans
                t={t}
                i18nKey={($) => $.onboarding["<cta>Click here</cta> to connect another Amazon Advertising account."]}
                components={{
                  cta: (
                    <LinkButton onPress={() => initAmazonAdvertisingAdditionalAccount()} css={{ display: "inline" }}>
                      {null}
                    </LinkButton>
                  ),
                }}
              />
            </p>
            <Flex justifyContent="center">
              <Button onPress={onClose}>{t(($) => $.common.actions.Close)}</Button>
            </Flex>
          </>
        }
      </TableErrorWrapper>
    </VStack>
  );
}

/** The loaded dialog: its table starts from what is known by then, the account to show among it. */
function ProfilePicker({
  rows,
  clients,
  accountOptions,
  defaultAccount,
  onClose,
  onSubmit,
  isSubmitting,
}: {
  rows: AmazonProfile[];
  clients: AmazonAdsClientProfiles[];
  accountOptions: AccountOption[];
  /** The account to start on; every account when undefined. */
  defaultAccount: number | undefined;
  onClose: () => void;
  onSubmit: (profiles: AmazonProfile[]) => void;
  isSubmitting: boolean;
}) {
  const { t } = useTranslation(["onboarding", "common"]);
  const hasAccountPicker = accountOptions.length > 1;

  // The account column, its filter and the search look a profile's account up here rather than copying it onto
  // every row. Each profile is listed under one account only.
  const clientByProfile = useMemo(() => {
    const byProfile = new Map<string, AmazonAdsClientProfiles>();
    for (const client of clients) {
      for (const profile of client.profiles) {
        byProfile.set(profileKey(profile), client);
      }
    }
    return byProfile;
  }, [clients]);
  const globalFilterFn = useMemo(
    () =>
      commaSearchFilterFn<AmazonProfile>((profile) => {
        const client = clientByProfile.get(profileKey(profile));
        return [
          profile.profile_name,
          profile.entity_id,
          client?.amazon_user_email,
          client?.amazon_user_name,
          client?.amazon_user_id,
        ].join(" ");
      }),
    [clientByProfile],
  );
  // With one account there is nothing to pick; it still shows, so it's clear where the profiles come from.
  const onlyAccount = accountOptions.length === 1 ? accountOptions[0] : undefined;

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "addAmazonProfilesTable",
    // Only lasting preferences are kept: column widths (double-clicking a resize handle resets one) and page size.
    // Each visit starts grouped by account, on the default account.
    skipPersisting: [
      "sorting",
      "columnVisibility",
      "columnFilters",
      "columnOrder",
      "columnPinning",
      "expanded",
      "globalFilter",
      "grouping",
      "isAllMatchingSelected",
      "rowPinning",
      "rowSelection",
    ],
    initialState: {
      sorting: [
        { id: "account", desc: false },
        { id: "profile_name", desc: false },
      ],
      columnFilters: defaultAccount === undefined ? [] : [{ id: "account", value: defaultAccount }],
      // The account column says where each profile comes from, so it shows only with every account in view.
      columnVisibility: { account: hasAccountPicker && defaultAccount === undefined },
    },
  });
  const showAccountColumn = state.columnVisibility.account ?? true;

  const columns = useMemo(
    () => [
      columnHelper.accessor("profile_name", {
        header: t(($) => $.onboarding["Profile Name"]),
        // Takes the account column's room when that is hidden.
        size: showAccountColumn ? 170 : 320,
        enableHiding: false,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor((profile) => profile.marketplaces.join(", "), {
        id: "marketplaces",
        header: t(($) => $.onboarding["Profile Marketplaces"]),
        size: 120,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor("profile_type", {
        header: t(($) => $.onboarding["Profile Type"]),
        size: 90,
        enableResizing: false,
        sortFn: "alphanumeric",
        cell: ({ getValue }) => snakeCaseToSentenceCase(getValue()),
      }),
      columnHelper.accessor((profile) => (profile.profile_type === "seller" ? profile.entity_id : undefined), {
        id: "seller_id",
        header: t(($) => $.onboarding["Seller ID"]),
        size: 140,
        enableResizing: false,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        cell: ({ row, getValue }) => {
          const sellerId = getValue();
          return sellerId ? <SellerIdLink sellerId={sellerId} marketplace={row.original.marketplaces[0]} /> : null;
        },
      }),
      // Sorts on the email, else the name, else the account ID.
      columnHelper.accessor(
        (profile) => {
          const client = clientByProfile.get(profileKey(profile));
          return client?.amazon_user_email ?? client?.amazon_user_name ?? client?.amazon_user_id ?? undefined;
        },
        {
          id: "account",
          header: t(($) => $.onboarding["Amazon account"]),
          size: 230,
          sortFn: "alphanumeric",
          sortUndefined: "last",
          // The account picker filters on the account itself, which several might share a label with.
          filterFn: (row, _columnId, clientId) => clientByProfile.get(profileKey(row.original))?.client_id === clientId,
          cell: ({ row }) => <AccountCell client={clientByProfile.get(profileKey(row.original))} />,
        },
      ),
    ],
    [t, showAccountColumn, clientByProfile],
  );

  const table = useClientTable({
    data: rows,
    columns,
    globalFilterFn,
    getRowId: (profile) => String(profile.profile_id),
    enableRowSelection: true,
    initialState,
    state,
    ...setters,
  });

  const accountColumn = table.getColumn("account");
  const account = (accountColumn?.getFilterValue() as number | undefined) ?? ALL_ACCOUNTS;
  const showAccount = (clientId: number | undefined) => {
    accountColumn?.setFilterValue(clientId);
    accountColumn?.toggleVisibility(clientId === undefined);
  };

  // Unlike the shared resetTable, keeps the selection: losing ticked profiles on a reset would be a nasty surprise.
  const resetView = () => {
    table.resetColumnFilters();
    table.resetColumnVisibility();
    table.resetGlobalFilter();
    table.resetSorting();
    table.resetPageIndex();
    table.resetPageSize();
    table.resetColumnSizing();
  };

  // Selections survive a search that hides them, so profiles can be picked across several searches; the
  // import button counts every selected profile, hidden or not. Unticking the header checkbox clears them all.
  const selectedProfiles = table.getSelectedRowModel().rows.map((row) => row.original);
  const visibleRowCount = table.getFilteredRowModel().rows.length;

  return (
    <>
      {/* The tabs take the room the modal has left and scroll inside, so the footer below stays in view. */}
      <Tabs defaultSelectedKey="profiles" display="flex" flexDirection="column" flex="1" minHeight="0">
        <TabList aria-label={t(($) => $.onboarding["Import Additional Amazon Profiles"])}>
          <Tab id="profiles">{t(($) => $.onboarding["Available Profiles"])}</Tab>
          {clients.length > 0 && <Tab id="credentials">{t(($) => $.onboarding["Ad Credentials"])}</Tab>}
        </TabList>
        <TabPanel id="profiles" className={tabPanelStyles}>
          {rows.length ?
            <VStack alignItems="stretch" gap="small" paddingTop="medium">
              {onlyAccount && (
                <VStack alignItems="stretch" gap="xsmall" marginBottom="small">
                  <span className={css({ color: "text.label", fontSize: "medium", fontWeight: "bold" })}>
                    {t(($) => $.onboarding["Amazon account"])}
                  </span>
                  <div
                    className={css({
                      border: "1px solid {colors.border.default}",
                      borderRadius: "light",
                      paddingY: "medium",
                      paddingX: "large",
                    })}
                  >
                    <AccountOptionContent option={onlyAccount} />
                  </div>
                </VStack>
              )}
              {hasAccountPicker && (
                <Select
                  label={t(($) => $.onboarding["Amazon account"])}
                  variant="card"
                  searchPlaceholder={t(($) => $.onboarding["Search by account ID, name or email"])}
                  items={accountOptions}
                  selectedKey={account}
                  onSelectionChange={(key) => showAccount(key === ALL_ACCOUNTS ? undefined : Number(key))}
                  css={{ marginBottom: "small" }}
                >
                  {(option) => (
                    // The list's search matches the text value, so it covers who added the account too.
                    <ListBoxItem
                      id={option.value}
                      textValue={`${option.label} ${option.byline}`}
                      className={accountItemStyles}
                    >
                      <AccountOptionContent option={option} />
                    </ListBoxItem>
                  )}
                </Select>
              )}
              <HStack gap="medium" flexWrap="wrap">
                <Button
                  variant="contentOnly"
                  aria-label={t(($) => $.common.table["Reset table filters"])}
                  onPress={resetView}
                  fontSize="xxlarge"
                >
                  <MdRotateLeft />
                </Button>
                <SearchInput
                  aria-label={t(($) => $.common.actions.Search)}
                  placeholder={t(($) => $.onboarding["Search for an Amazon Advertising profile..."])}
                  value={table.state.globalFilter ?? ""}
                  onChange={table.setGlobalFilter}
                  css={{ flex: "1 1 300px" }}
                />
                <RT8Pagination table={table} />
              </HStack>
              {visibleRowCount ?
                <Table table={table} />
              : <Message>{t(($) => $.onboarding["No profiles match your search."])}</Message>}
            </VStack>
          : <Message>
              {t(
                ($) =>
                  $.onboarding[
                    "There are no new profiles available to import from your connected Amazon Advertising accounts."
                  ],
              )}
            </Message>
          }
        </TabPanel>
        {clients.length > 0 && (
          <TabPanel id="credentials" className={tabPanelStyles}>
            <CredentialList clients={clients} />
          </TabPanel>
        )}
      </Tabs>

      <p className={css({ textAlign: "center", color: "text.secondary", paddingX: "xlarge" })}>
        <Trans
          t={t}
          i18nKey={($) =>
            $.onboarding[
              "Not seeing the profile you're looking to connect? <cta>Click here</cta> to connect another Amazon Advertising account, or check the Profile Health panel in Amazon Profile Summary for read-only access or payment method issues."
            ]
          }
          components={{
            cta: (
              <LinkButton onPress={() => initAmazonAdvertisingAdditionalAccount()} css={{ display: "inline" }}>
                {null}
              </LinkButton>
            ),
          }}
        />
      </p>

      <Flex justifyContent="center" gap="xlarge">
        <Button variant="secondary" onPress={onClose}>
          {t(($) => $.common.actions.Cancel)}
        </Button>
        <Button
          onPress={() => onSubmit(selectedProfiles)}
          isDisabled={!selectedProfiles.length || isSubmitting}
          isPending={isSubmitting}
        >
          {t(($) => $.onboarding["Import {count} Profiles"], { count: selectedProfiles.length })}
        </Button>
      </Flex>
    </>
  );
}
